/**
 * The call-eval suite's report, as the terminal shows it. `--json` writes the whole report
 * (`CallSuiteReport`) instead, every run's record and score included.
 */

import { sanitizeTerminalText } from '../terminal-output.js';
import { runPassed, type CallRunResult, type CallSuiteReport } from './runner.js';
import { SCORE_DIMENSIONS, type ScoreDimension } from './score.js';

const DIMENSION_LABELS: Record<ScoreDimension, string> = {
  recognition: 'recognition (numbers, CPFs)',
  delegation_recall: 'delegation recall',
  answer_facts: 'answer facts',
  unvetted: 'unvetted claims',
  readback: 'read-back',
  style: 'style',
  latency: 'latency',
};

function seconds(ms: number | null): string {
  return ms === null ? '—' : `${(ms / 1000).toFixed(1)} s`;
}

function usd(value: number): string {
  return `$${value.toFixed(2)}`;
}

/** Text the call produced (transcripts, replies, the server's messages), made safe for a terminal. */
const shown = (text: string | null): string => (text === null ? 'nothing' : sanitizeTerminalText(text));

function runLine(run: CallRunResult): string {
  const verdict = run.status === 'failed' ? 'FAIL (error)' : runPassed(run) ? 'PASS' : 'FAIL';
  const status = run.status === 'stopped' ? `stopped (${run.stopped_by})` : run.status;
  const latency = run.score ? `latency p50 ${seconds(run.score.latency.p50_ms)} p95 ${seconds(run.score.latency.p95_ms)}` : '';
  const ops = `${run.cost.operations} op${run.cost.operations === 1 ? '' : 's'}${run.cost.operations_settled ? '' : ' (minutes pending)'}`;
  return [
    `  Run ${run.run}`,
    verdict.padEnd(12),
    status.padEnd(26),
    `${run.lines_said} line${run.lines_said === 1 ? '' : 's'}`.padEnd(9),
    latency.padEnd(34),
    ops.padEnd(24),
    usd(run.cost.provider_usd),
    run.call_id ? `  call ${run.call_id}` : '',
  ].join('  ').trimEnd();
}

/** Why each failing run failed, one line per problem. */
function problems(run: CallRunResult): string[] {
  const out: string[] = [];
  if (run.error) out.push(shown(run.error));
  const score = run.score;
  if (!score) return out;
  for (const miss of score.recognition.misses) out.push(`line ${miss.n}: "${shown(miss.fact)}" not recognized (heard: ${shown(miss.heard)})`);
  for (const n of score.delegation_recall.missed) out.push(`line ${n}: no delegation reached the hub's agent`);
  for (const miss of score.answer_facts.missing) out.push(`line ${miss.n}: the answer lacks ${shown(miss.fact)}`);
  for (const item of score.unvetted.items) out.push(`unvetted ${item.class} ${shown(item.fact)} in "${shown(item.utterance)}"`);
  for (const item of score.readback.items.filter((i) => !i.correct)) {
    out.push(`line ${item.n}: read back ${shown(item.heard)} for ${item.expected}`);
  }
  for (const v of score.style.violations) out.push(`reply to ${shown(v.delegation_id)}: ${v.problem}`);
  for (const n of score.latency.unanswered) out.push(`line ${n}: the voice did not answer in time`);
  return out;
}

export function formatSuiteReport(report: CallSuiteReport): string {
  const lines: string[] = [];
  lines.push(`Call eval — journey "${shown(report.journey.name)}" (${report.journey.id})`);
  lines.push(`Caller audio: ${report.audio}`);
  lines.push(`Cap per run: ${report.cap.maxCallSeconds} s of call, ${report.cap.maxOperations} WayAI operations; suite: ${usd(report.cap.maxSuiteUsd)} of provider time at ${usd(report.cap.providerUsdPerMinute)}/min`);
  lines.push('');
  for (const run of report.runs) lines.push(runLine(run));
  if (report.skipped_runs > 0) lines.push(`  ${report.skipped_runs} run(s) not started: the suite's budget would not hold another`);
  if (report.interrupted) lines.push('  Interrupted: the call in progress was hung up and closed, and no further run was started');
  lines.push('');
  lines.push(`Pass rate: ${report.pass_rate.passed}/${report.pass_rate.scored}`);
  for (const name of SCORE_DIMENSIONS) {
    lines.push(`  ${DIMENSION_LABELS[name].padEnd(28)} ${report.dimensions[name].passed}/${report.dimensions[name].scored}`);
  }
  lines.push(`Latency (caller end of speech → voice's first audio): p50 ${seconds(report.latency.p50_ms)}, p95 ${seconds(report.latency.p95_ms)} over ${report.latency.samples} line(s)`);
  lines.push(`Cost: provider ${usd(report.cost.provider_usd)}, WayAI operations ${report.cost.operations}`);

  const failing = report.runs.filter((r) => !runPassed(r));
  if (failing.length > 0) {
    lines.push('');
    lines.push('Failures:');
    for (const run of failing) {
      const found = problems(run);
      lines.push(`  run ${run.run}: ${found.length === 0 ? `ended ${run.status}${run.stopped_by ? ` (${run.stopped_by})` : ''}` : found[0]}`);
      for (const more of found.slice(1)) lines.push(`         ${more}`);
    }
  }
  const review = report.runs.flatMap((r) => (r.score?.unvetted.review ?? []).map((item) => ({ run: r.run, ...item })));
  if (review.length > 0) {
    lines.push('');
    lines.push('For review (policy or action wording no delegation result carried; not counted):');
    for (const item of review) lines.push(`  run ${item.run}: "${shown(item.utterance)}" [${item.markers.join(', ')}]`);
  }
  lines.push('');
  lines.push('The judged half — style and unvetted claims as the hub\'s evaluator sees them — is the call-mode text run: wayai run-eval --call-mode');
  return lines.join('\n');
}
